import React, { useEffect, useMemo, useState } from "react";
import { Modal, Card, Stack, Skeleton, List, ListItemButton, Chip } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import KendoInput from "assets/theme/components/inputKendo";
import { SubscriptionPlansApiService } from "api/subscriptionPlans.api";
import LocationTreePickerModal from "pages/AssetsManagement/AssetsListings/LocationTreePickerModal";

const containerStyles = {
  width: { xs: "96%", md: "60%", lg: "45%" },
  maxHeight: "82vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: 2,
  p: 3,
};

// SubscriptionPlans.planType — mirrors DefaultPlanCatalog.PlanTypes.
const PLAN_TYPE_LABELS = { 1: "Per site", 2: "Enterprise", 3: "Add-on" };

// A plan is sold on whichever bases it prices; offer exactly those.
const termOptionsFor = (plan) => {
  if (!plan) return [];
  const options = [];
  if (plan.price != null) options.push({ value: "monthly", label: "Monthly" });
  if (plan.annualPrice != null) options.push({ value: "yearly", label: "Yearly" });
  if (plan.flatPrice != null) options.push({ value: "flat", label: "Flat rate" });
  return options;
};

const rateFor = (plan, term) => {
  if (!plan) return 0;
  if (term === "yearly") return Number(plan.annualPrice ?? 0);
  if (term === "flat") return Number(plan.flatPrice ?? 0);
  return Number(plan.price ?? 0);
};

// Mirrors flatTermLabel on the catalog page.
const flatTermLabel = (plan) => {
  if (plan?.flatTermMonths) {
    const m = plan.flatTermMonths;
    if (m % 12 === 0) {
      const y = m / 12;
      return `${y} ${y === 1 ? "year" : "years"}`;
    }
    return `${m} ${m === 1 ? "month" : "months"}`;
  }
  if (plan?.flatEndDate) return `until ${String(plan.flatEndDate).slice(0, 10)}`;
  return "no end date";
};

// One line summarising every basis a plan is sold on, for the picker rows.
const priceSummary = (p) => {
  const parts = [];
  if (p?.price != null) parts.push(`$${Number(p.price).toLocaleString()}/mo`);
  if (p?.annualPrice != null) parts.push(`$${Number(p.annualPrice).toLocaleString()}/yr`);
  if (p?.flatPrice != null)
    parts.push(`$${Number(p.flatPrice).toLocaleString()} flat (${flatTermLabel(p)})`);
  return parts.join(" · ");
};

// Field label rendered ABOVE the input (never a floating MUI label).
function FieldLabel({ children }) {
  return (
    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
      {children}
    </SoftTypography>
  );
}

FieldLabel.propTypes = { children: PropTypes.node.isRequired };

// Picks a platform subscription plan onto a quote as a lineKind:"subscription"
// line. Quoting does NOT grant the subscription — whoever closes the sale
// still assigns it in backoffice under Environmental → Subscriptions, which is
// why the site below is optional and only a label on the quote.
const AddSubscriptionPickerModal = ({ open, handleClose, onAddItem, customerOrganisationId }) => {
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [quantity, setQuantity] = useState("1");
  const [term, setTerm] = useState("monthly");
  const [selected, setSelected] = useState(null);
  const [site, setSite] = useState(null); // { id, name } | null
  const [sitePickerOpen, setSitePickerOpen] = useState(false);

  // The catalog is nine rows — fetch once on open and filter in memory rather
  // than round-tripping per keystroke the way the Part/Service pickers do.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await SubscriptionPlansApiService.softwareCatalog();
        if (cancelled) return;
        const payload = response?.data?.data?.data ?? response?.data?.data ?? [];
        setPlans(Array.isArray(payload) ? payload : []);
      } catch (error) {
        if (cancelled) return;
        toast.error(error?.response?.data?.message || "Failed to load subscription plans");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setKeyword("");
      setQuantity("1");
      setTerm("monthly");
      setSelected(null);
      setSite(null);
      setPlans([]);
    }
  }, [open]);

  const visiblePlans = useMemo(() => {
    const needle = keyword.trim().toLowerCase();
    if (!needle) return plans;
    return plans.filter((p) =>
      `${p?.name ?? ""} ${p?.uuid ?? ""} ${p?.description ?? ""}`.toLowerCase().includes(needle)
    );
  }, [plans, keyword]);

  const termOptions = useMemo(() => termOptionsFor(selected), [selected]);

  // Picking a plan resets the term to the first basis it actually offers —
  // a flat-only plan must never sit on a stale "monthly".
  useEffect(() => {
    if (!selected) return;
    const options = termOptionsFor(selected);
    if (!options.some((o) => o.value === term)) {
      setTerm(options[0]?.value ?? "monthly");
    }
  }, [selected, term]);

  const rate = useMemo(() => rateFor(selected, term), [selected, term]);

  const qtyNum = Math.max(0, Number(quantity) || 0);

  const handleAdd = () => {
    if (!selected) {
      toast.error("Pick a plan first.");
      return;
    }
    if (qtyNum <= 0) {
      toast.error("Quantity must be greater than zero.");
      return;
    }
    onAddItem({
      lineKind: "subscription",
      subscriptionPlanId: selected.id,
      subscriptionTerm: term,
      // Snapshot the plan's flat term onto the line — repricing the plan later
      // must not rewrite what this client was quoted.
      subscriptionTermMonths: term === "flat" ? selected.flatTermMonths ?? null : null,
      subscriptionEndDate:
        term === "flat" && selected.flatEndDate
          ? String(selected.flatEndDate).slice(0, 10)
          : null,
      subscriptionLocationId: site?.id ?? null,
      subscriptionLocationName: site?.name ?? null,
      subscriptionPlanUuid: selected.uuid,
      name: site?.name ? `${selected.name} — ${site.name}` : selected.name,
      subscriptionTermLabel: term === "flat" ? flatTermLabel(selected) : null,
      itemNo: selected.uuid || `PLAN-${selected.id}`,
      description: selected.description || "",
      quantity: String(qtyNum),
      rate: String(rate),
      price: String(rate * qtyNum),
    });
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <SoftBox sx={containerStyles}>
        <Card sx={{ p: 2, boxShadow: "none" }}>
          <SoftTypography variant="h5" mb={2}>
            Add Subscription to Quotation
          </SoftTypography>

          <KendoInput
            placeholder="Search plans by name or SKU…"
            type="search"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setSelected(null);
            }}
          />

          <SoftBox mt={2} sx={{ minHeight: 240 }}>
            {loading ? (
              <Skeleton variant="rectangular" height={240} animation="wave" />
            ) : visiblePlans.length === 0 ? (
              <SoftBox p={2} textAlign="center">
                <SoftTypography variant="caption">
                  No plans in the software catalog. A platform admin seeds them
                  with POST /api/scripts/plan-catalog/seed-defaults.
                </SoftTypography>
              </SoftBox>
            ) : (
              <List
                dense
                sx={{
                  maxHeight: 280,
                  overflowY: "auto",
                  border: "1px solid #eee",
                  borderRadius: 1,
                }}
              >
                {visiblePlans.map((p) => {
                  const isSelected = selected?.id === p.id;
                  return (
                    <ListItemButton
                      key={p.id}
                      selected={isSelected}
                      onClick={() => setSelected(p)}
                      sx={{
                        borderBottom: "1px solid #f0f0f0",
                        "&.Mui-selected": { backgroundColor: "#E3F2FD" },
                      }}
                    >
                      <SoftBox flex={1}>
                        <SoftBox display="flex" alignItems="center" gap={1}>
                          <SoftTypography variant="body2" fontWeight="medium" fontSize={13}>
                            {p.name}
                          </SoftTypography>
                          <Chip
                            label={PLAN_TYPE_LABELS[p.planType] || "Plan"}
                            size="small"
                            sx={{
                              height: 18,
                              backgroundColor: "#E8F0FE",
                              color: "#1A56C0",
                              fontWeight: 600,
                              fontSize: 10,
                            }}
                          />
                        </SoftBox>
                        <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                          {p.uuid} — {priceSummary(p)}
                          {p.description ? ` — ${p.description}` : ""}
                        </SoftTypography>
                      </SoftBox>
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </SoftBox>

          {selected && (
            <SoftBox mt={2}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <SoftBox flex={1}>
                  <FieldLabel>Term</FieldLabel>
                  <Select
                    value={termOptions.some((t) => t.value === term) ? term : ""}
                    onChange={(e) => setTerm(e.target.value)}
                    fullWidth
                    sx={{ height: 40 }}
                  >
                    {termOptions.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {term === "flat" && (
                    <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                      {flatTermLabel(selected)}
                    </SoftTypography>
                  )}
                </SoftBox>
                <SoftBox flex={1}>
                  <FieldLabel>Quantity</FieldLabel>
                  <KendoInput
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                </SoftBox>
                <SoftBox flex={1}>
                  <FieldLabel>Rate</FieldLabel>
                  <SoftBox
                    sx={{
                      bgcolor: "#fafafa",
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                      border: "1px solid #eee",
                    }}
                  >
                    <SoftTypography variant="body2">${rate.toFixed(2)}</SoftTypography>
                  </SoftBox>
                </SoftBox>
              </Stack>

              <SoftBox mt={2}>
                <FieldLabel>Site (optional)</FieldLabel>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SoftBox
                    flex={1}
                    sx={{
                      bgcolor: "#fafafa",
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                      border: "1px solid #eee",
                    }}
                  >
                    <SoftTypography variant="body2">
                      {site?.name || "Whole organization"}
                    </SoftTypography>
                  </SoftBox>
                  <SoftButton
                    variant="outlined"
                    color="info"
                    size="small"
                    disabled={!customerOrganisationId}
                    onClick={() => setSitePickerOpen(true)}
                  >
                    Choose
                  </SoftButton>
                  {site && (
                    <SoftButton
                      variant="text"
                      color="dark"
                      size="small"
                      onClick={() => setSite(null)}
                    >
                      Clear
                    </SoftButton>
                  )}
                </Stack>
                <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                  {customerOrganisationId
                    ? "A per-site plan is granted one site at a time. Recording it here tells whoever assigns the subscription which site the sale covers."
                    : "Pick the customer organisation first to choose one of their sites."}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end" mt={3}>
            <SoftButton variant="outlined" color="dark" onClick={handleClose}>
              Cancel
            </SoftButton>
            <SoftButton color="success" variant="contained" onClick={handleAdd} disabled={!selected}>
              Add Subscription
            </SoftButton>
          </Stack>
        </Card>

        <LocationTreePickerModal
          open={sitePickerOpen}
          onClose={() => setSitePickerOpen(false)}
          organizationId={customerOrganisationId}
          selectedLocationId={site?.id ?? null}
          showClearButton={false}
          title="Select site"
          onSelect={(picked) => {
            setSite(picked ? { id: picked.id, name: picked.name } : null);
            setSitePickerOpen(false);
          }}
        />
      </SoftBox>
    </Modal>
  );
};

AddSubscriptionPickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onAddItem: PropTypes.func.isRequired,
  // The quote's selected customer org — scopes the site picker. Null until the
  // seller has chosen one, which disables the picker rather than showing the
  // seller's own tree.
  customerOrganisationId: PropTypes.number,
};

AddSubscriptionPickerModal.defaultProps = {
  customerOrganisationId: null,
};

export default AddSubscriptionPickerModal;
